import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'

import { cn } from '#/shared/lib/utils'
import { isWorkspaceTabsInteraction } from '#/shared/lib/workspace-tabs'
import { Button } from '#/shared/ui/button'

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    // z-70 (modal layer): float above the detail sidebar (z-60) and all app
    // surfaces (z-50). See the z-index scale in app/styles/index.css.
    // `app-frame-inset` keeps the scrim inside the pinned workspace card
    // (no-op / full-viewport when unpinned).
    className={cn('app-frame-inset fixed z-70 bg-black/40', className)}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  overlayClassName?: string
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    { className, overlayClassName, children, onInteractOutside, ...props },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed top-14 left-1/2 z-70 flex max-h-[calc(100vh-7rem)] w-full max-w-[920px] -translate-x-1/2 flex-col overflow-hidden p-0',
          'rounded-md bg-white shadow-popover focus:outline-none',
          className,
        )}
        onInteractOutside={(event) => {
          // The workspace-tabs chrome floats above the modal layer: a click
          // there means "switch workspace", not "dismiss" — the target
          // workspace's own per-tab state decides what it shows.
          if (isWorkspaceTabsInteraction(event.target)) {
            event.preventDefault()
            return
          }
          onInteractOutside?.(event)
        }}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-base font-semibold leading-none text-gray-900',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-gray-500', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

interface DialogHeaderProps {
  title: string
  cancelLabel?: string
  submitLabel?: string
  onSubmit?: () => void
  submitDisabled?: boolean
}

function DialogHeader({
  title,
  cancelLabel = 'Cancel',
  submitLabel = 'Create',
  onSubmit,
  submitDisabled,
}: DialogHeaderProps) {
  return (
    <header className="flex shrink-0 items-center justify-between gap-2.5 border-b border-gray-200 px-5 py-3">
      <DialogTitle>{title}</DialogTitle>
      <div className="flex items-center gap-2.5">
        <DialogClose asChild>
          <Button
            type="button"
            variant="outline"
            size="xs"
            pressEffect
            className="px-3.5"
          >
            {cancelLabel}
          </Button>
        </DialogClose>
        {onSubmit && (
          <Button
            type="button"
            size="xs"
            pressEffect
            className="px-3.5"
            disabled={submitDisabled}
            onClick={onSubmit}
          >
            {submitLabel}
          </Button>
        )}
      </div>
    </header>
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
export type { DialogHeaderProps }
